import Required from 'components/Required';
import { CreditCard, Landmark, Banknote } from 'lucide-react';
import React, { useEffect, useState, ChangeEvent } from 'react';
import {
  FormGroup,
  Row,
  Col,
  CardTitle,
  Button,
  Label,
  Input,
} from 'reactstrap';
import { useAlertPlus } from 'hooks';
import { MatriculaRequest } from 'types/Matriculas';
import { DatePickerChangeEvent } from 'components/DatePicker';
import AlertModal from 'components/AlertModal';

interface Props {
  data: Partial<MatriculaRequest>;
  isLoading: boolean;
  onBack: () => void;
  onNext: () => void;
  formValidation: Partial<Record<keyof MatriculaRequest, boolean>>;
  handleInputChange: (event: ChangeEvent<HTMLInputElement> | DatePickerChangeEvent) => void;
  validateStep: (requiredFields: string[], isForm?: boolean) => boolean;
  disableSend: boolean;
}

const StepThree = ({
  data,
  isLoading,
  onBack,
  onNext,
  formValidation,
  handleInputChange,
  validateStep,
  disableSend,
}: Props) => {
  const [showAlert, message, alertCallback, alert] = useAlertPlus('');

  const optionsFormaPago = [
    {
      label: 'EFECTIVO POR MERCADO PAGO',
      icon: <Banknote size={26} />,
      value: 'efectivo',
    },
    {
      label: 'DÉBITO AUTOMÁTICO EN CUENTA BANCARIA CON CBU',
      icon: <Landmark size={26} />,
      value: 'bancaria',
    },
    {
      label: 'DÉBITO AUTOMÁTICO EN TARJETA DE CRÉDITO',
      icon: <CreditCard size={26} />,
      value: 'tarjeta',
    },
  ];

  const [selectedForma, setSelectedForma] = useState<string>();

  useEffect(() => {
    setSelectedForma(data.forma_pago);
  }, []);

  const handleSelectPayment = (value: string) => {
    data.forma_pago = value;
    setSelectedForma(value);
  };

  const validateStepThree = (): boolean => {
    const requiredFields = ['forma_pago'];
    if (!validateStep(requiredFields, false)) {
      return false;
    }

    switch (selectedForma) {
      case 'bancaria':
        requiredFields.push('numero_tarjeta_cbu');
        break;
      case 'tarjeta':
        requiredFields.push('tipo_tarjeta');
        requiredFields.push('numero_tarjeta_cbu');
        break;
    }
    if (!validateStep(requiredFields, true)) {
      return false;
    }

    if (data.forma_pago !== 'efectivo') {
      const field = document.getElementById('numero_tarjeta_cbu');

      if (data.forma_pago === 'bancaria') {
        if (data.numero_tarjeta_cbu?.length !== 22) {
          field?.classList.add('is-invalid');
          alert(
            'El CBU debe tener 22 dígitos.',
          );
          return false;
        }
      } else if (data.forma_pago === 'tarjeta') {
        if (data.numero_tarjeta_cbu?.length !== 16) {
          field?.classList.add('is-invalid');
          alert(
            'El número de tarjeta debe tener 16 dígitos.',
          );
          return false;
        }
      }
    }

    return true;
  };

  return (
    <div style={{ padding: '0 10rem 1rem 10rem' }}>
      {/* Forma de pago */}
      <Col sm={12}>
        <CardTitle
          style={{
            fontSize: '1rem',
            fontWeight: 'normal',
          }}
        >Forma de pago de Matrícula <Required /></CardTitle>
        <CardTitle>
          Selecciona la modalidad con la que abonarás la matrícula.
        </CardTitle>
      </Col>

      <Row className='mt-3'>
        {optionsFormaPago.map(({ label, icon, value }) => {
          // const isActive = data.forma_pago === label;
          const isActive = selectedForma === value;
          return (
            <Col
              key={label}
              md='4'
              className='mb-2 mt-4 d-flex justify-content-center'
            >
              <div
                onClick={() => handleSelectPayment(value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '10px',
                  padding: '1rem 1rem',
                  borderRadius: '10px',
                  border: `2px solid ${isActive ? '#6f42c1' : 'gray'}`,
                  backgroundColor: isActive ? '#ccbaf1' : 'white',
                  color: isActive ? '#6f42c1' : '#0c0c0c',
                  fontWeight: 'bold',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '100%',
                  height: 80,
                }}
              >
                {icon}
                <span
                  style={{
                    fontSize: 12,
                    textAlign: 'left',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    color: 'black',
                  }}
                >
                  {label}
                </span>
              </div>
            </Col>
          );
        })}
      </Row>
      { data.forma_pago === 'tarjeta' && (
        <Col sm={12} className='mt-4'>
          <CardTitle>Ingresá los datos de tu tarjeta de crédito</CardTitle>

          <FormGroup>
            <Row className='mt-3'>
              <Col md={6}>
                <Label
                  style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
                  for='tipo_tarjeta'
                >
                  Tarjetas Aceptadas <Required />
                </Label>
                <Input
                  id='tipo_tarjeta'
                  name='tipo_tarjeta'
                  type='select'
                  className='custom-select form-control'
                  value={data.tipo_tarjeta ?? ''}
                  invalid={formValidation.tipo_tarjeta === false}
                  onChange={handleInputChange}
                  disabled={isLoading || disableSend}
                  required
                >
                  <option value=''>
                    Seleccione un tipo...
                  </option>
                  <option value='VISA'>VISA</option>
                  <option value='MasterCard'>MasterCard</option>
                </Input>
              </Col>
              <Col md={6}>
                <Label
                  style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
                  for='numero_tarjeta_cbu'
                >
                  Número de tarjeta de crédito <Required />
                </Label>
                <Input
                  id='numero_tarjeta_cbu'
                  name='numero_tarjeta_cbu'
                  type='number'
                  value={data.numero_tarjeta_cbu ?? ''}
                  disabled={isLoading || disableSend}
                  invalid={formValidation.numero_tarjeta_cbu === false}
                  onChange={handleInputChange}
                  required
                />
              </Col>
            </Row>
          </FormGroup>
        </Col>
      )}
      { data.forma_pago === 'bancaria' && (
        <Col sm={12} className='mt-4'>
          <CardTitle>Ingresá los datos de tu banco</CardTitle>

          <FormGroup>
            <Row className='mt-3'>
              <Col md={6}>
                <Label
                  style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
                  for='numero_tarjeta_cbu'
                >
                  CBU <Required />
                </Label>
                <Input
                  id='numero_tarjeta_cbu'
                  name='numero_tarjeta_cbu'
                  type='number'
                  value={data.numero_tarjeta_cbu ?? ''}
                  disabled={isLoading || disableSend}
                  invalid={formValidation.numero_tarjeta_cbu === false}
                  onChange={handleInputChange}
                  required
                />
              </Col>
            </Row>
          </FormGroup>
        </Col>
      )}

      <div
        style={{ marginTop: '5rem' }}
        className='d-flex justify-content-center'
      >
        <Button
          type='button'
          className='btn-custom'
          disabled={isLoading || disableSend}
          onClick={ onBack }
        >
          Anterior
        </Button>
        <Button
          type='button'
          className='btn-custom'
          disabled={isLoading}
          onClick={() => {
            if (validateStepThree()) onNext();
          }}
        >
          Siguiente
        </Button>
      </div>
      <AlertModal
        isOpen={showAlert}
        message={message}
        onClose={alertCallback}
      />
    </div>
  );
};

export default StepThree;
